import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { dateTypeMap, getTimeSpaceArray } from '@op/utils';
import { DBO_TM } from '@op/utils/format';
import { DBOM_POINT } from '@opm/operationLog/buriedPoint';
import { gotoDownload } from '@opm/pages/Download';
import { getMemesTableList } from '@opm/services';
import { useEffect, useMemo, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';

/**
 * Memes 列表数据 Hook
 * 按照 API 文档 /api/v1/marketing/memes/list 实现
 */
export function useMemesTableData({
  filter,
  defaultPageInfo = { page: 1, page_size: 20 },
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
}) {
  // Tab 状态：按 views 或 likes 统计维度
  const [tabKey, setTabKey] = useState<'views' | 'likes'>('views');

  // 关键字搜索
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');

  // 排序状态
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'views',
  });

  // 分页
  const { pageInfo, changePageInfo, resetPageInfo } = usePageInfo({
    defaultPageInfo,
  });

  // 数据
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);

  // 请求状态
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    clear: () => {
      setData([]);
      setTotal(0);
    },
  });

  // 切换排序
  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
    resetPageInfo();
  }

  // 切换关键字
  function changeKeyword(newKeyword: string) {
    setKeyword(newKeyword);
    resetPageInfo();
  }

  // 切换 Tab
  function changeTabKey(newTabKey: 'views' | 'likes') {
    setTabKey(newTabKey);
    // 切换 tab 时更新默认排序字段
    changeSortBy({ order: 'desc', by: newTabKey }); // API sort_item: views | likes
  }

  // 请求参数 - 按照 API 文档字段
  const params = useMemo(() => {
    if (filter) {
      return {
        ...filter,
        ...pageInfo,
        sort_item: sortByRef.current?.by,
        sort_model: sortByRef.current?.order,
        search_text: keywordRef.current,
        stat_type: tabKey, // 统计维度：views | likes
        should_include_timeline: true, // 需要趋势数据
      };
    }

    return null;
  }, [pageInfo]);

  // 发起请求
  function request() {
    if (!params) return;
    wrapperCancelRequest(getMemesTableList, params)
      .then(response => {
        const { list = [], total = 0 } = response || {};
        // Pre-process: flatten video_stats_timelines[0] into video_stats object
        // and filter timeline points within [start_time, end_time]
        const formattedList = list.map((record: any) => {
          const stats = record?.video_stats_timelines?.[0] || {};
          const { timeline_of_time } = stats;
          const {
            timeline_of_start_time: startTime,
            timeline_of_end_time: endTime,
          } = timeline_of_time || {};
          const rawTimeline = stats?.timeline || [];

          // Build complete date series using getTimeSpaceArray,
          // filling missing dates with value: 0
          let filteredTimeline = rawTimeline;
          if (startTime && endTime) {
            const dateType =
              dateTypeMap[params?.date_type as keyof typeof dateTypeMap] ||
              'day';
            const format =
              dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
            const timelineMap = new Map(
              rawTimeline.map((item: any) => [
                DBO_TM.sFormat(item.time, format),
                item,
              ]),
            );

            const dateArr = getTimeSpaceArray(
              startTime,
              endTime,
              dateType,
              format,
            );
            filteredTimeline = dateArr.map((dateKey: string) => {
              return (
                timelineMap.get(dateKey) || {
                  time: DBO_TM.sFormat(dateKey, format),
                  value: 0,
                }
              );
            });
          }

          return {
            ...record,
            video_stats: {
              ...stats,
              timeline: filteredTimeline,
            },
          };
        });
        setData(formattedList);
        setTotal(total);
      })
      .catch(() => {
        // 请求失败时不做特殊处理
      });
  }

  // 监听参数变化发起请求
  useEffect(() => {
    request();
  }, [params]);

  // 下载
  function download() {
    gotoDownload({
      key: 'MarketingMemesDownload',
      fileName: `Meme_Analysis_${DBO_TM.timeTitle(params)}`,
      filter: params,
    });
    DBOM_POINT.btn('export_data_click', { type: 'list' });
  }

  return {
    data,
    total,
    pageInfo,
    changePageInfo,
    resetPageInfo,
    loading,
    sortBy,
    setSortBy: changeSortBy,
    keyword,
    setKeyword: changeKeyword,
    download,
    tabKey,
    setTabKey: changeTabKey,
  };
}
